import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { Button } from "../../../../components/shared/Buttons"
import Typography from "../../../../components/shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import ImagePickerV2 from "../../components/shared/ImagePickerV2"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import { getDefaultTextLogo, TextLogoEditor } from "../components/TextLogoEditor"
import { DEFAULT_LOGO_FONT } from "../constants/logoFonts"

const TAB_UPLOAD = "upload"
const TAB_CUSTOM = "custom"

const SLIDER_MIN = 32
const SLIDER_MAX = 150
const MAIN_FONT_SIZE_MIN = 14
const MAIN_FONT_SIZE_MAX = 56
const SECOND_FONT_SIZE_MIN = 8
const SECOND_FONT_SIZE_MAX = 40
const LETTER_SPACING_MIN = -5 // -0.05em
const LETTER_SPACING_MAX = 50 // 0.5em
const LINE_SPACING_MAX = 24

/**
 * Section label + description block (Figma "Builder / Image-single" style).
 */
function SectionBlock({ title, description, children, className = "" }) {
  return (
    <div className={`border-b border-gray-light pb-6 ${className}`}>
      <div className="mb-4">
        <Typography variant="h5" className="mb-0.5 font-bold text-gray-dark">
          {title}
        </Typography>
        {description && (
          <Typography variant="small" className="text-gray">
            {description}
          </Typography>
        )}
      </div>
      {children}
    </div>
  )
}

/**
 * Slider row with label and optional value display (px or custom suffix).
 */
function SliderRow({ label, value, min = SLIDER_MIN, max = SLIDER_MAX, onChange, showValue = true, suffix = "px" }) {
  return (
    <div className="flex items-center gap-4">
      {label && (
        <Typography variant="small" className="min-w-28 font-bold text-gray-dark">
          {label}
        </Typography>
      )}
      <input
        type="range"
        min={String(min)}
        max={String(max)}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-light slider"
      />
      {showValue && (
        <Typography variant="small" className="min-w-12 font-medium text-gray-dark">
          {suffix === "em" ? `${(value / 100).toFixed(2)}em` : `${value}${suffix}`}
        </Typography>
      )}
    </div>
  )
}

/**
 * Sitewide Logo form matching Figma HM Pro Experience (Logo section).
 * Tabs: Upload | Custom. Upload: Header logo, Footer logo, Header/Footer logo size.
 * Custom: Create a logo (one/two lines), Main/Second line, line spacing, alignment, sizes.
 *
 * Theme fields: logoType ("image" | "text"), logo, whiteLogo, textLogo (when logoType === "text").
 * Existing themes without these fields keep showing image/practice name; header/footer only use
 * text logo when textLogoBuilderEnabled and theme has logoType === "text" and textLogo.
 */
const LogoForm = ({ theme, practice: practiceProp, onUpdate, onClose }) => {
  const { practice: contextPractice, textLogoBuilderEnabled } = useWebsiteBuilderV2()
  const currentPractice = contextPractice ?? practiceProp
  const showCustomTab = textLogoBuilderEnabled === true

  const [activeTab, setActiveTab] = useState(
    showCustomTab && theme?.header?.logoType === "text" ? TAB_CUSTOM : TAB_UPLOAD
  )
  const [logo, setLogo] = useState(theme?.header?.logo || null)
  const [whiteLogo, setWhiteLogo] = useState(theme?.header?.whiteLogo || null)
  const [headerLogoSize, setHeaderLogoSize] = useState(theme?.header?.logoSize ?? 72)
  const [footerLogoSize, setFooterLogoSize] = useState(theme?.footer?.logoSize ?? 72)

  const [textLinesMode, setTextLinesMode] = useState(theme?.header?.textLogo?.secondLine ? "two" : "one")
  const [textLogo, setTextLogo] = useState(
    theme?.header?.textLogo
      ? {
          mainLine: theme?.header?.textLogo?.mainLine ?? "",
          secondLine: theme?.header?.textLogo?.secondLine ?? "",
          mainFont: theme?.header?.textLogo?.mainFont ?? DEFAULT_LOGO_FONT,
          secondFont: theme?.header?.textLogo?.secondFont ?? DEFAULT_LOGO_FONT
        }
      : getDefaultTextLogo(currentPractice?.name)
  )
  const [mainFontSize, setMainFontSize] = useState(theme?.header?.textLogo?.mainFontSize ?? 24)
  const [mainLetterSpacing, setMainLetterSpacing] = useState(theme?.header?.textLogo?.mainLetterSpacing ?? 0)
  const [secondFontSize, setSecondFontSize] = useState(theme?.header?.textLogo?.secondFontSize ?? 14)
  const [secondLetterSpacing, setSecondLetterSpacing] = useState(theme?.header?.textLogo?.secondLetterSpacing ?? 10)
  const [logoLineSpacing, setLogoLineSpacing] = useState(theme?.header?.textLogo?.lineSpacing ?? 0)
  const [fontAlignment, setFontAlignment] = useState(theme?.header?.textLogo?.alignment ?? "left")

  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  useEffect(() => {
    setActiveTab(showCustomTab && theme?.header?.logoType === "text" ? TAB_CUSTOM : TAB_UPLOAD)
    setLogo(theme?.header?.logo || null)
    setWhiteLogo(theme?.header?.whiteLogo || null)
    setHeaderLogoSize(theme?.header?.logoSize ?? 72)
    setFooterLogoSize(theme?.footer?.logoSize ?? 72)
    setTextLinesMode(theme?.header?.textLogo?.secondLine ? "two" : "one")
    if (theme?.header?.textLogo) {
      const tl = theme?.header?.textLogo
      setTextLogo({
        mainLine: tl?.mainLine ?? "",
        secondLine: tl?.secondLine ?? "",
        mainFont: tl?.mainFont ?? DEFAULT_LOGO_FONT,
        secondFont: tl?.secondFont ?? DEFAULT_LOGO_FONT
      })
      setMainFontSize(tl?.mainFontSize ?? 24)
      setMainLetterSpacing(tl?.mainLetterSpacing ?? 0)
      setSecondFontSize(tl?.secondFontSize ?? 14)
      setSecondLetterSpacing(tl?.secondLetterSpacing ?? 10)
      setLogoLineSpacing(tl?.lineSpacing ?? 0)
      setFontAlignment(tl?.alignment ?? "left")
    } else {
      setTextLogo(getDefaultTextLogo(currentPractice?.name))
      setMainFontSize(24)
      setMainLetterSpacing(0)
      setSecondFontSize(14)
      setSecondLetterSpacing(10)
      setLogoLineSpacing(0)
      setFontAlignment("left")
    }
  }, [theme, currentPractice?.name, showCustomTab])

  const useTextLogo = showCustomTab && activeTab === TAB_CUSTOM
  const logoData = {
    logoType: useTextLogo ? "text" : "image",
    logo: !useTextLogo ? logo : null,
    whiteLogo: !useTextLogo ? whiteLogo : null,
    logoSize: headerLogoSize,
    textLogo: useTextLogo
      ? {
          ...textLogo,
          secondLine: textLinesMode === "two" ? textLogo.secondLine : "",
          mainFontSize,
          mainLetterSpacing,
          secondFontSize,
          secondLetterSpacing,
          lineSpacing: logoLineSpacing,
          alignment: fontAlignment
        }
      : null
  }
  const footerLogoData = {
    logoType: useTextLogo ? "text" : "image",
    logoSize: footerLogoSize,
    logo: !useTextLogo ? logo : null,
    whiteLogo: !useTextLogo ? whiteLogo : null,
    textLogo: useTextLogo
      ? {
          ...textLogo,
          secondLine: textLinesMode === "two" ? textLogo.secondLine : "",
          mainFontSize,
          mainLetterSpacing,
          secondFontSize,
          secondLetterSpacing,
          lineSpacing: logoLineSpacing,
          alignment: fontAlignment
        }
      : null
  }

  useEffect(() => {
    onUpdate(
      {
        header: { ...theme?.header, ...logoData },
        footer: { ...theme?.footer, ...footerLogoData }
      },
      { temporary: true }
    )
  }, [
    activeTab,
    logo,
    whiteLogo,
    headerLogoSize,
    footerLogoSize,
    textLogo,
    mainFontSize,
    mainLetterSpacing,
    secondFontSize,
    secondLetterSpacing,
    logoLineSpacing,
    fontAlignment,
    textLinesMode
  ])

  const handleHeaderImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle,
      description: "header_logo",
      provider: provider || "filestack"
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        setLogo(result.data.uploadImage.photoUrl)
        setActiveTab(TAB_UPLOAD)
        showToast("Header logo uploaded successfully!")
      } else {
        const errorMessage =
          result?.data?.uploadImage?.errors ||
          "There was an error uploading your logo. Please try again later or contact support."
        showToast({ type: "error", content: errorMessage })
      }
    })
  }

  const handleFooterImageSelect = ({ handle, provider }) => {
    uploadImage({
      handle,
      description: "footer_logo",
      provider: provider || "filestack"
    }).then((result) => {
      if (result?.data?.uploadImage?.result === "success") {
        setWhiteLogo(result.data.uploadImage.photoUrl)
        setActiveTab(TAB_UPLOAD)
        showToast("Footer logo uploaded successfully!")
      } else {
        const errorMessage =
          result?.data?.uploadImage?.errors ||
          "There was an error uploading your logo. Please try again later or contact support."
        showToast({ type: "error", content: errorMessage })
      }
    })
  }

  const handleHeaderImageRemove = () => {
    setLogo(null)
    showToast("Header logo removed.")
  }

  const handleFooterImageRemove = () => {
    setWhiteLogo(null)
    showToast("Footer logo removed.")
  }

  const handleSave = () => {
    onUpdate({
      header: { ...theme?.header, ...logoData },
      footer: { ...theme?.footer, ...footerLogoData }
    })
    onClose()
  }

  const handleCancelClick = () => {
    onUpdate({ header: theme?.header || {}, footer: theme?.footer || {} }, { temporary: true })
    onClose()
  }

  return (
    <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
      <div className="px-8 pt-6">
        {/* Tab Bar — Upload | Custom (only when text logo builder enabled) */}
        <div className="mb-6 flex gap-8 border-b border-gray-light">
          <button
            type="button"
            onClick={() => setActiveTab(TAB_UPLOAD)}
            className={`pb-2 text-base font-bold focus:outline-none ${
              activeTab === TAB_UPLOAD ? "border-b-2 border-gray-dark text-gray-dark" : "text-teal"
            }`}>
            Upload
          </button>
          {showCustomTab && (
            <button
              type="button"
              onClick={() => setActiveTab(TAB_CUSTOM)}
              className={`pb-2 text-base font-bold focus:outline-none ${
                activeTab === TAB_CUSTOM ? "border-b-2 border-gray-dark text-gray-dark" : "text-teal"
              }`}>
              Custom
            </button>
          )}
        </div>

        {activeTab === TAB_UPLOAD && (
          <div className="space-y-6">
            <SectionBlock title="Header logo" description="Logo shown in the header of your site.">
              {logo ? (
                <div className="mb-6 flex items-center justify-center">
                  <div className="flex items-center gap-4">
                    <img src={logo} alt="Header logo" className="size-16 rounded-lg object-contain" />
                    <div className="flex flex-col gap-2">
                      <ImagePickerV2
                        practice={currentPractice}
                        onImageSelect={handleHeaderImageSelect}
                        fetching={uploadFetching}
                        showCurateTab={true}
                        currentImageUrl={logo}
                        trigger={
                          <button className="font-bold text-builder-accent hover:underline" disabled={uploadFetching}>
                            Edit image
                          </button>
                        }
                      />
                      <button
                        type="button"
                        className="font-bold text-red-500 hover:underline"
                        onClick={handleHeaderImageRemove}
                        disabled={uploadFetching}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ImagePickerV2
                  practice={currentPractice}
                  onImageSelect={handleHeaderImageSelect}
                  fetching={uploadFetching}
                  showCurateTab={true}
                  currentImageUrl={logo}
                  trigger={
                    <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                      <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                        <svg
                          className="size-5 text-builder-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-accent">
                        <p className="whitespace-pre leading-[24px]">
                          <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                        </p>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                        <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                      </div>
                    </div>
                  }
                />
              )}
            </SectionBlock>

            <SectionBlock title="Footer logo" description="Logo shown in the footer of your site.">
              {whiteLogo ? (
                <div className="mb-6 flex items-center justify-center">
                  <div className="flex items-center gap-4">
                    <div className="size-16 shrink-0 overflow-hidden rounded-lg border border-gray bg-gray-dark object-contain">
                      <img src={whiteLogo} alt="Footer logo" className="size-full object-contain" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <ImagePickerV2
                        practice={currentPractice}
                        onImageSelect={handleFooterImageSelect}
                        fetching={uploadFetching}
                        showCurateTab={true}
                        currentImageUrl={whiteLogo}
                        trigger={
                          <button className="font-bold text-builder-accent hover:underline" disabled={uploadFetching}>
                            Edit image
                          </button>
                        }
                      />
                      <button
                        type="button"
                        className="font-bold text-red-500 hover:underline"
                        onClick={handleFooterImageRemove}
                        disabled={uploadFetching}>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <ImagePickerV2
                  practice={currentPractice}
                  onImageSelect={handleFooterImageSelect}
                  fetching={uploadFetching}
                  showCurateTab={true}
                  currentImageUrl={whiteLogo}
                  trigger={
                    <div className="relative box-border flex w-full shrink-0 cursor-pointer flex-col content-stretch items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-builder-border bg-white p-6 transition-colors hover:border-builder-accent">
                      <div className="relative flex size-10 shrink-0 items-center justify-center text-clip rounded-[100px] bg-builder-bg-light">
                        <svg
                          className="size-5 text-builder-accent"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-center font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[0] text-builder-accent">
                        <p className="whitespace-pre leading-[24px]">
                          <span className="font-['Manrope:Bold',_sans-serif] font-bold">upload an image</span>
                        </p>
                      </div>
                      <div className="relative shrink-0 text-nowrap text-left font-['Manrope:Regular',_sans-serif] text-[13px] font-normal leading-[0] text-builder-secondary">
                        <p className="block whitespace-pre leading-[19.5px]">Supported formats: PNG, JPG, SVG</p>
                      </div>
                    </div>
                  }
                />
              )}
            </SectionBlock>

            <SectionBlock title="Header logo size" description="Adjust the logo size within the header.">
              <SliderRow value={headerLogoSize} onChange={setHeaderLogoSize} />
            </SectionBlock>

            <SectionBlock title="Footer logo size" description="Adjust the logo size within the footer.">
              <SliderRow value={footerLogoSize} onChange={setFooterLogoSize} />
            </SectionBlock>
          </div>
        )}

        {activeTab === TAB_CUSTOM && (
          <div className="space-y-6">
            <SectionBlock title="Create a logo" description="Add your business name and select a font." />

            <div className="flex gap-6 border-b border-gray-light pb-6">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="textLinesMode"
                  value="one"
                  checked={textLinesMode === "one"}
                  onChange={() => setTextLinesMode("one")}
                  className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                />
                <span className="text-base text-gray-dark">One line of text</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="textLinesMode"
                  value="two"
                  checked={textLinesMode === "two"}
                  onChange={() => setTextLinesMode("two")}
                  className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                />
                <span className="text-base text-gray-dark">Two lines of text</span>
              </label>
            </div>

            <TextLogoEditor
              value={textLogo}
              onChange={(v) => setTextLogo((prev) => ({ ...prev, ...v }))}
              previewColor="#FFFFFF"
              previewBackgroundColor="#374151"
              previewMainFontSize={mainFontSize}
              previewSecondFontSize={textLinesMode === "two" ? secondFontSize : undefined}
              previewMainLetterSpacing={mainLetterSpacing}
              previewSecondLetterSpacing={secondLetterSpacing}
              previewLineSpacing={logoLineSpacing}
              previewAlignment={fontAlignment}
            />

            {/* Main line — Font size & letter spacing */}
            <div className="space-y-4 border-b border-gray-light pb-6">
              <div className="rounded-lg border border-gray-light bg-gray-ultralight p-5">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <Typography variant="small" className="font-bold text-gray-dark">
                      Font size
                    </Typography>
                    <div className="w-[208px]">
                      <SliderRow
                        value={mainFontSize}
                        min={MAIN_FONT_SIZE_MIN}
                        max={MAIN_FONT_SIZE_MAX}
                        onChange={setMainFontSize}
                        showValue={true}
                        suffix="px"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Typography variant="small" className="font-bold text-gray-dark">
                      Letter spacing
                    </Typography>
                    <div className="w-[208px]">
                      <SliderRow
                        value={mainLetterSpacing}
                        min={LETTER_SPACING_MIN}
                        max={LETTER_SPACING_MAX}
                        onChange={setMainLetterSpacing}
                        showValue={true}
                        suffix="em"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second line sizing (only when two lines) */}
            {textLinesMode === "two" && (
              <>
                <div className="space-y-4 border-b border-gray-light pb-6">
                  <div className="rounded-lg border border-gray-light bg-gray-ultralight p-5">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <Typography variant="small" className="font-bold text-gray-dark">
                          Font size
                        </Typography>
                        <div className="w-[208px]">
                          <SliderRow
                            value={secondFontSize}
                            min={SECOND_FONT_SIZE_MIN}
                            max={SECOND_FONT_SIZE_MAX}
                            onChange={setSecondFontSize}
                            showValue={true}
                            suffix="px"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Typography variant="small" className="font-bold text-gray-dark">
                          Letter spacing
                        </Typography>
                        <div className="w-[208px]">
                          <SliderRow
                            value={secondLetterSpacing}
                            min={LETTER_SPACING_MIN}
                            max={LETTER_SPACING_MAX}
                            onChange={setSecondLetterSpacing}
                            showValue={true}
                            suffix="em"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <SectionBlock
                  title="Logo line spacing"
                  description="Adjust the distance between the main and second line of text.">
                  <SliderRow value={logoLineSpacing} min={0} max={LINE_SPACING_MAX} onChange={setLogoLineSpacing} />
                </SectionBlock>
              </>
            )}

            <SectionBlock title="Font alignment">
              <div className="flex gap-6">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="fontAlignment"
                    value="left"
                    checked={fontAlignment === "left"}
                    onChange={() => setFontAlignment("left")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="text-base font-bold text-gray-dark">Left</span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="fontAlignment"
                    value="center"
                    checked={fontAlignment === "center"}
                    onChange={() => setFontAlignment("center")}
                    className="size-5 border-gray-300 text-teal form-radio focus:ring-teal"
                  />
                  <span className="text-base text-gray-dark">Center</span>
                </label>
              </div>
            </SectionBlock>

            <SectionBlock title="Header logo size" description="Adjust the logo size within the header.">
              <SliderRow value={headerLogoSize} onChange={setHeaderLogoSize} />
            </SectionBlock>

            <SectionBlock title="Footer logo size" description="Adjust the logo size within the footer.">
              <SliderRow value={footerLogoSize} onChange={setFooterLogoSize} />
            </SectionBlock>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-gray-light bg-white px-8 py-6">
        <Button type="secondary" onClick={handleCancelClick} className="px-6">
          Close
        </Button>
        <Button type="primary" onClick={handleSave} className="px-6">
          Save
        </Button>
      </div>
    </div>
  )
}

export default LogoForm
